import React, { useEffect, useState } from 'react';
import Icon from '../components/ui/Icon';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import { usePopup } from '../context/PopupContext';
import './StudentsPro.css';

const PAGE_SIZE = 15;

const statusPill = (status) => {
  const map = {
    'placed': { bg: '#e5f4ef', color: '#059669' },
    'not placed': { bg: '#fdeaea', color: '#dc2626' },
    'in process': { bg: '#fbf0e0', color: '#d97706' }
  };
  const s = map[status] || { bg: '#f1f5f9', color: '#64748b' };
  return <span className="smx-pill" style={{ background: s.bg, color: s.color }}>{status || 'unknown'}</span>;
};

const Students = () => {
  const navigate = useNavigate();
  const { showConfirm, showAlert } = usePopup();

  // Server-side pagination (audit M16): the database filters, sorts and slices; this page only ever holds one page of
  // rows plus the whole-college summary and the filter options the API returns.
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [summary, setSummary] = useState({ total: 0, placed: 0, not_placed: 0, arrears: 0, avg_cgpa: null, branches: 0 });
  const [options, setOptions] = useState({ branches: [], batch_years: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [branch, setBranch] = useState('all');
  const [batch, setBatch] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [reloadTick, setReloadTick] = useState(0);
  const fetchStudents = () => setReloadTick(n => n + 1);

  useEffect(() => { const h = setTimeout(() => setDebouncedSearch(search.trim()), 300); return () => clearTimeout(h); }, [search]);

  // Filters/sort changing returns to page 1 and clears the selection, so bulk actions can never act on rows the admin
  // can no longer see.
  useEffect(() => { setPage(1); setSelected([]); }, [debouncedSearch, branch, batch, status, sort]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    const params = { page, limit: PAGE_SIZE, sort: sort.key, dir: sort.dir };
    if (debouncedSearch) params.search = debouncedSearch;
    if (branch !== 'all') params.branch = branch;
    if (batch !== 'all') params.batch_year = batch;
    if (status !== 'all') params.status = status;
    API.get('/students', { params })
      .then(res => {
        if (cancelled) return;
        setRows(res.data.data || []); setMeta(res.data.metadata || { total: 0, total_pages: 1 });
        setSummary(res.data.stats || {}); setOptions(res.data.filters || { branches: [], batch_years: [], statuses: [] });
      })
      .catch(err => { if (!cancelled) setError(err.response?.data?.message || 'Failed to load students.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, debouncedSearch, branch, batch, status, sort, reloadTick]);

  const branches = options.branches, batches = options.batch_years, statuses = options.statuses;
  const totalPages = Math.max(1, meta.total_pages || 1);
  const pageRows = rows;
  const stats = {
    total: summary.total ?? 0, placed: summary.placed ?? 0, notPlaced: summary.not_placed ?? 0, arrears: summary.arrears ?? 0,
    avgCgpa: summary.avg_cgpa == null ? '—' : Number(summary.avg_cgpa).toFixed(2), branches: summary.branches ?? 0,
  };

  const toggleSort = (key) => setSort(p => p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const arrow = (key) => sort.key === key ? (sort.dir === 'asc' ? ' (asc)' : ' (desc)') : '';
  // Accessible, keyboard-operable sortable column header.
  const ariaSort = (key) => (sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none');
  const sortProps = (key, extra = '') => ({
    className: `sortable ${extra}`.trim(), scope: 'col', role: 'columnheader', tabIndex: 0, 'aria-sort': ariaSort(key),
    onClick: () => toggleSort(key),
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(key); } }
  });

  const clearAll = () => { setSearch(''); setBranch('all'); setBatch('all'); setStatus('all'); };
  const activeFilters = (search ? 1 : 0) + (branch !== 'all' ? 1 : 0) + (batch !== 'all' ? 1 : 0) + (status !== 'all' ? 1 : 0);

  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const pageIds = pageRows.map(s => s.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.includes(id));
  const toggleSelectPage = () => setSelected(p => allPageSelected ? p.filter(id => !pageIds.includes(id)) : [...new Set([...p, ...pageIds])]);

  const handleDelete = async (id, name) => {
    if (!(await showConfirm('Delete Student', `Delete ${name || 'this student'}? This deactivates the account.`))) return;
    try { await API.delete(`/students/${id}`); setSelected(p => p.filter(x => x !== id)); fetchStudents(); }
    catch { showAlert('Error', 'Failed to delete student'); }
  };

  const handleBulkDelete = async () => {
    if (!(await showConfirm('Delete Selected', `Delete ${selected.length} selected student(s)?`))) return;
    try { await API.delete('/students/bulk', { data: { ids: selected } }); setSelected([]); fetchStudents(); }
    catch { showAlert('Error', 'Failed to delete selected students'); }
  };

  return (
    <div className="smx-page">
      <div className="smx-top">
        <div>
          <h1>Student Management</h1>
          <p>{meta.total} of {stats.total} students{activeFilters > 0 && <button className="smx-clear" onClick={clearAll}> · Clear ({activeFilters})</button>}</p>
        </div>
        <div className="smx-actions">
          {selected.length > 0 && <button className="smx-btn danger" onClick={handleBulkDelete}>Delete Selected ({selected.length})</button>}
          <button className="smx-btn" onClick={() => navigate('/admin/students/add')}>Bulk Upload</button>
          <button className="smx-btn primary" onClick={() => navigate('/admin/students/add')}>+ Add Student</button>
        </div>
      </div>

      <div className="smx-stats">
        <div className="smx-stat"><div className="v">{stats.total}</div><div className="l">Total</div></div>
        <div className="smx-stat"><div className="v" style={{ color: '#059669' }}>{stats.placed}</div><div className="l">Placed</div></div>
        <div className="smx-stat"><div className="v" style={{ color: '#dc2626' }}>{stats.notPlaced}</div><div className="l">Not Placed</div></div>
        <div className="smx-stat"><div className="v" style={{ color: '#d97706' }}>{stats.arrears}</div><div className="l">With Arrears</div></div>
        <div className="smx-stat"><div className="v">{stats.avgCgpa}</div><div className="l">Avg CGPA</div></div>
        <div className="smx-stat"><div className="v">{stats.branches}</div><div className="l">Branches</div></div>
      </div>

      <div className="smx-toolbar">
        <input className="smx-search" type="search" aria-label="Search students by name, email or roll number" placeholder="Search by name, email, roll no…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="smx-select" value={branch} onChange={e => setBranch(e.target.value)}><option value="all">All Branches</option>{branches.map(b => <option key={b} value={b}>{b}</option>)}</select>
        <select className="smx-select" value={batch} onChange={e => setBatch(e.target.value)}><option value="all">All Batches</option>{batches.map(b => <option key={b} value={b}>{b}</option>)}</select>
        <select className="smx-select" value={status} onChange={e => setStatus(e.target.value)}><option value="all">All Status</option>{statuses.map(s => <option key={s} value={s}>{s}</option>)}</select>
      </div>

      {loading ? (
        <div className="smx-table-wrap" style={{ padding: 16 }}>{[...Array(8)].map((_, i) => <div key={i} className="smx-skel" />)}</div>
      ) : error ? (
        <div className="smx-table-wrap"><div className="smx-state"><Icon name="warning" size={18} /> {error}<br /><button className="smx-btn" style={{ marginTop: 12 }} onClick={fetchStudents}>Retry</button></div></div>
      ) : (
        <div className="smx-table-wrap">
          <div style={{ overflowX: 'auto' }}>
            <table className="smx-table">
              <thead>
                <tr>
                  <th style={{ width: 34 }} scope="col"><input type="checkbox" aria-label="Select all students on this page" checked={allPageSelected} onChange={toggleSelectPage} /></th>
                  <th {...sortProps('name')}>Name{arrow('name')}</th>
                  <th {...sortProps('roll_number')}>Roll No{arrow('roll_number')}</th>
                  <th className="smx-hide-sm" scope="col">Branch</th>
                  <th className="smx-hide-sm" scope="col">Batch</th>
                  <th {...sortProps('cgpa', 'smx-hide-sm')}>CGPA{arrow('cgpa')}</th>
                  <th {...sortProps('arrears', 'smx-hide-sm')}>Arrears{arrow('arrears')}</th>
                  <th className="smx-hide-sm" scope="col">Email</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={10}><div className="smx-state">No students match your filters.</div></td></tr>
                ) : pageRows.map(s => (
                  <tr key={s.id}>
                    <td><input type="checkbox" aria-label={`Select ${s.name}`} checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)} /></td>
                    <td><span className="smx-name" onClick={() => navigate(`/admin/students/${s.id}`)}>{s.name}</span></td>
                    <td>{s.roll_number || '—'}</td>
                    <td className="smx-hide-sm">{s.branch ? <span className="smx-badge">{s.branch}</span> : '—'}</td>
                    <td className="smx-hide-sm">{s.placement_batch_year || '—'}</td>
                    <td className="smx-hide-sm" style={{ fontWeight: 600 }}>{Number(s.cgpa) > 0 ? Number(s.cgpa).toFixed(2) : '—'}</td>
                    <td className="smx-hide-sm" style={{ color: Number(s.arrears) > 0 ? '#dc2626' : '#059669', fontWeight: 600 }}>{s.arrears ?? 0}</td>
                    <td className="smx-hide-sm" style={{ color: '#64748b' }}>{s.email}</td>
                    <td>{statusPill(s.placement_status)}</td>
                    <td>
                      <div className="smx-row-actions">
                        <button className="smx-icon-btn" onClick={() => navigate(`/admin/students/${s.id}`)}>View</button>
                        <button className="smx-icon-btn" onClick={() => navigate(`/admin/students/edit/${s.id}`)}>Edit</button>
                        <button className="smx-icon-btn del" onClick={() => handleDelete(s.id, s.name)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="smx-pagination">
            <span>Page {page} of {totalPages} · {meta.total} result{meta.total !== 1 ? 's' : ''}</span>
            <div className="smx-page-btns">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;
